/**
 * Google Analytics 4, loaded only after the visitor says yes.
 *
 * The measurement id comes from a `ga4` tracking code on the Region in the CMS, so each
 * market reports into its own property and a market with no such entry loads nothing at
 * all. There is no default id: a default is how one region ends up silently reporting
 * into another's.
 *
 * **Why GA4 is here rather than pasted in as a snippet like every other tag.** Google's
 * own snippet sends a page view when it loads and never again; this is a single-page app,
 * so that is one view for the landing page and nothing for the rest of the visit. Running
 * it from the id lets `trackPageView` fire per navigation - see App.tsx. Anything else a
 * market wants goes in as a snippet, in `lib/tags.ts`.
 *
 * **The script is not loaded until analytics consent is granted.** Google's Consent
 * Mode would allow loading gtag up front in a denied state, but "loaded and told not
 * to" is a harder thing to explain to four data protection authorities than "not
 * loaded". Consent Mode is still set - denied by default, analytics granted on
 * consent, advertising left denied because this site runs no ads - so that behaviour
 * is explicit rather than implied by the absence of the tag.
 *
 * Withdrawing consent cannot unload a script that is already running, so it does the
 * two things that can be done: turns collection off through Consent Mode, and deletes
 * the `_ga` cookies. The Consent Mode signal itself is pushed once for the whole page by
 * `App.tsx`, not here - GA4 and a Tag Manager container share one `dataLayer`, and each
 * pushing its own view of the decision is how they end up disagreeing.
 */
import { dropCookies } from './consent';
import { gtag, pushConsentDefaults } from './gtag';

const SCRIPT_ID = 'weco-ga4';

let loadedId: string | null = null;
let granted = false;

function ensureLoaded(measurementId: string) {
  if (loadedId === measurementId) return;

  // Consent Mode defaults, pushed before the tag so nothing is stored on the way in.
  // `lib/gtag.ts` owns them because a Tag Manager container may have pushed them already,
  // and a second `consent default` after a Google tag is running is ignored.
  pushConsentDefaults();
  gtag('js', new Date());
  // Page views are sent by hand: this is a single-page app, so the one the tag would
  // send on load is the only one it would ever see.
  gtag('config', measurementId, { send_page_view: false, anonymize_ip: true });

  if (!document.getElementById(SCRIPT_ID)) {
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
    document.head.appendChild(script);
  }

  loadedId = measurementId;
}

/**
 * Apply a consent decision.
 *
 * Called on every change, including the first one, and safe to call repeatedly.
 */
export function applyAnalyticsConsent(measurementId: string | null, allowed: boolean) {
  if (!measurementId) return;

  if (allowed) {
    ensureLoaded(measurementId);
    granted = true;
    return;
  }

  granted = false;
  dropCookies(['_ga', '_gid', '_gat']);
}

/**
 * One page view. Does nothing until consent has been granted.
 *
 * Returns whether it actually sent, so the caller can tell "counted" from "dropped
 * because we had no permission yet" - the page the visitor was on when they accepted
 * would otherwise never be counted at all.
 */
export function trackPageView(path: string, title: string): boolean {
  if (!granted || !loadedId) return false;
  gtag('event', 'page_view', {
    page_path: path,
    page_title: title,
    page_location: window.location.href
  });
  return true;
}
